import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Icons
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  XCircle,
} from "lucide-react";

// Components
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";

type InputState = {
  email?: string;
  token?: string;
  password: string;
  password_confirmation: string;
};

const resetPasswordFn = async (newData: InputState) => {
  const response = await api.post("/auth/reset-password", newData);
  return response.data;
};

export default function ForgotPasswordResetPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailParams = searchParams.get("email");
  const tokenParams = searchParams.get("token");
  const tagParams = searchParams.get("tag");

  // State
  const [form, setForm] = useState<InputState>({
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isReseted, setIsReseted] = useState(false);
  const [passStrength, setPassStrength] = useState(0);

  // Mutation
  const { mutate, isPending, error } = useMutation({
    mutationFn: resetPasswordFn,
    onSuccess: (data) => {
      setIsReseted(true);
      toast.success(data?.message || "Password berhasil diubah.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan yang tidak terduga.";
      toast.error(errorMessage);
    },
  });

  // Handlers
  const handleResetPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (form.password !== form.password_confirmation) {
      return toast.error("Konfirmasi password tidak cocok.");
    }
    mutate({ ...form, email: emailParams || "", token: tokenParams || "" });
  };

  // Auto redirect after success
  useEffect(() => {
    if (isReseted) {
      const timer = setTimeout(() => {
        navigate(`/login${tagParams ? `?tag=${tagParams}` : ""}`);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isReseted, navigate, tagParams]);

  // Simple password strength calculator
  useEffect(() => {
    let strength = 0;
    if (form.password.length > 5) strength += 30;
    if (form.password.length > 7) strength += 20;
    if (/[A-Z]/.test(form.password)) strength += 25;
    if (/[0-9]/.test(form.password)) strength += 25;
    setPassStrength(strength);
  }, [form.password]);

  // Validasi URL (mencegah akses tanpa token)
  if (!emailParams || !tokenParams) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 px-4 transition-colors duration-300">
        <div className="text-center space-y-4 bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-lg border dark:border-slate-800 max-w-md w-full">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="w-8 h-8" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Link Tidak Valid
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Tautan reset password ini tidak lengkap atau sudah kadaluarsa.
            Silakan ajukan permintaan baru.
          </p>
          <Link to="/forgot-password">
            <Button
              variant="outline"
              className="w-full mt-4 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Kembali ke Lupa Password
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50/50 dark:bg-slate-950 flex items-center justify-center p-4 transition-colors duration-300">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
        {/* State: SUKSES */}
        {isReseted ? (
          <div className="p-10 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6 animate-bounce-subtle" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t("guest.layout.reset-password.success.title")}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8">
              {t("guest.layout.reset-password.success.description")}
            </p>
            <Button
              onClick={() =>
                navigate(`/login${tagParams ? `?tag=${tagParams}` : ""}`)
              }
              className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white"
            >
              {t("guest.layout.reset-password.success.button")}
            </Button>
          </div>
        ) : (
          /* State: FORM */
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {t("guest.layout.reset-password.title")}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                {t("guest.layout.reset-password.description")}
              </p>
            </div>

            <form
              onSubmit={handleResetPassword}
              className="flex flex-col gap-y-5"
            >
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="dark:text-slate-200">
                  {t("guest.layout.reset-password.label.password")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t(
                      "guest.layout.reset-password.placeholder.password",
                    )}
                    className="pl-10 pr-10 dark:bg-slate-950 dark:border-slate-800 dark:text-slate-200 dark:placeholder:text-slate-600"
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Password Strength Bar (Simple) */}
                {form.password && (
                  <div className="space-y-1">
                    <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passStrength < 50
                            ? "bg-red-500"
                            : passStrength < 80
                              ? "bg-yellow-500"
                              : "bg-green-500"
                        }`}
                        style={{ width: `${passStrength}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                      {t("guest.layout.reset-password.strength.label")}{" "}
                      {passStrength < 50
                        ? t("guest.layout.reset-password.strength.weak")
                        : passStrength < 80
                          ? t("guest.layout.reset-password.strength.medium")
                          : t("guest.layout.reset-password.strength.strong")}
                    </p>
                  </div>
                )}

                <InputError
                  text={error?.response?.data.errors?.password?.join(",")}
                />
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label
                  htmlFor="password_confirmation"
                  className="dark:text-slate-200"
                >
                  {t("guest.layout.reset-password.label.confirm-password")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="confirm"
                    type={showPassword ? "text" : "password"}
                    placeholder={t(
                      "guest.layout.reset-password.placeholder.confirm-password",
                    )}
                    className={`pl-10 dark:bg-slate-950 dark:border-slate-800 dark:text-slate-200 dark:placeholder:text-slate-600 ${
                      form.password_confirmation &&
                      form.password !== form.password_confirmation
                        ? "border-red-500 focus-visible:ring-red-500 dark:border-red-500"
                        : ""
                    }`}
                    value={form.password_confirmation}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        password_confirmation: e.target.value,
                      })
                    }
                  />
                </div>
                {form.password_confirmation &&
                  form.password !== form.password_confirmation && (
                    <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Password tidak cocok
                    </p>
                  )}
                <InputError
                  text={error?.response?.data.errors?.password_confirmation?.join(
                    ",",
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-200/50 dark:shadow-none transition-all hover:scale-[1.02] active:scale-[0.98]"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>
                      {t("guest.layout.reset-password.button.processing")}
                    </span>
                  </div>
                ) : (
                  t("guest.layout.reset-password.button.submit")
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to={`/forgot-password${tagParams ? `?tag=${tagParams}` : ""}`}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center justify-center gap-2 group transition-colors"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                {t("guest.layout.reset-password.link.back")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
